import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSalons,
  changeSalonStatus,
  deleteSalon,
} from "../../store/slices/salonSlice";
import { salonService } from "../../services/salonService";
import { subscriptionService } from "../../services/subscriptionService";
import Icon from "../../components/Common/Icon";
import AdvancedTable from "../../components/Common/AdvancedTable";
import ConfirmationModal from "../../components/Common/ConfirmationModal";
import { useToast } from "../../context/ToastContext";

const STATUS_STYLES = {
  ACTIVE: "bg-success/15 text-success border border-success/20",
  TRIAL: "bg-purple-500/15 text-purple-600 border border-purple-500/20",
  SUSPENDED: "bg-tier-gold/15 text-tier-gold border border-tier-gold/20",
  INACTIVE: "bg-outline-variant/30 text-on-surface-variant border border-outline-variant/40",
};

const STATUS_LABELS = {
  ACTIVE: "Aktif",
  TRIAL: "Deneme Süresinde",
  SUSPENDED: "Askıda",
  INACTIVE: "Pasif",
};

// Lisans bitişine kalan güne göre metin + renk (kırmızı: <=30 gün/doldu, yeşil: aktif).
const licenseBadge = (expiry) => {
  if (!expiry) return { text: "—", cls: "bg-outline-variant/30 text-on-surface-variant border border-outline-variant/40" };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(expiry) - today) / 86400000);
  const dateStr = new Date(expiry).toLocaleDateString("tr-TR");
  
  if (days <= 30) {
    const textSuffix = days < 0 ? "Süresi doldu" : `${days} gün kaldı`;
    return {
      text: `${dateStr} (${textSuffix})`,
      cls: "bg-error/15 text-error border border-error/20"
    };
  }
  
  return {
    text: `${dateStr} (${days} gün kaldı)`,
    cls: "bg-success/15 text-success border border-success/20"
  };
};

export default function SalonsPage() {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { items, isLoading, error } = useSelector((state) => state.salon);
  const user = useSelector((state) => state.auth.user);
  const isAdmin = user?.role === "ADMIN";

  const [selectedSalonId, setSelectedSalonId] = useState(null);
  const [salonDetails, setSalonDetails] = useState(null);
  const [packageHistory, setPackageHistory] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  const [packages, setPackages] = useState([]);
  const [upgradePackageId, setUpgradePackageId] = useState("");
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);

  const [renewMonths, setRenewMonths] = useState(12);
  const [renewLoading, setRenewLoading] = useState(false);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null,
  });

  useEffect(() => {
    dispatch(fetchSalons());
    // Load packages for upgrade dropdown if admin
    if (isAdmin) {
      subscriptionService.list().then((res) => {
        setPackages(res.data.data ?? []);
      });
    }
  }, [dispatch, isAdmin]);

  // Load details & history when modal opens
  const handleOpenDetails = async (id) => {
    setSelectedSalonId(id);
    setDetailsLoading(true);
    setDetailsError(null);
    setUpgradeError(null);
    setUpgradePackageId("");
    try {
      const [detailsRes, historyRes] = await Promise.all([
        salonService.get(id),
        salonService.getPackageHistory(id),
      ]);
      setSalonDetails(detailsRes.data.data);
      setPackageHistory(historyRes.data.data ?? []);
    } catch (err) {
      setDetailsError(err.response?.data?.error ?? "Salon detayları yüklenemedi.");
      showToast("Salon detayları yüklenemedi.", "error");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleUpgradeSubmit = (e) => {
    e.preventDefault();
    if (!upgradePackageId) return;

    const selectedPkg = packages.find((p) => p.id === upgradePackageId);

    setConfirmModal({
      isOpen: true,
      title: "Paket Yükseltme Onayı",
      message: `Salon paketini "${selectedPkg?.name ?? "seçili paket"}" olarak değiştirmek istediğinize emin misiniz? Bu işlem salon paket geçmişine kaydedilecektir.`,
      type: "info",
      onConfirm: async () => {
        setUpgradeLoading(true);
        setUpgradeError(null);
        try {
          await salonService.upgrade(selectedSalonId, upgradePackageId);
          // Re-fetch details & history
          const [detailsRes, historyRes] = await Promise.all([
            salonService.get(selectedSalonId),
            salonService.getPackageHistory(selectedSalonId),
          ]);
          setSalonDetails(detailsRes.data.data);
          setPackageHistory(historyRes.data.data ?? []);
          setUpgradePackageId("");
          showToast("Salon paketi başarıyla güncellendi.", "success");
          // Refresh parent list
          dispatch(fetchSalons());
        } catch (err) {
          const errMsg = err.response?.data?.error ?? "Paket yükseltme başarısız.";
          setUpgradeError(errMsg);
          showToast(errMsg, "error");
        } finally {
          setUpgradeLoading(false);
        }
      },
    });
  };

  const handleRenewSubmit = (e) => {
    e.preventDefault();
    const months = Number(renewMonths);
    setConfirmModal({
      isOpen: true,
      title: "Lisans Yenileme Onayı",
      message: `Salon lisansını ${months} ay uzatmak istediğinize emin misiniz?${
        salonDetails?.status === "SUSPENDED" ? " Salon yeniden aktifleştirilecektir." : ""
      }`,
      type: "info",
      onConfirm: async () => {
        setRenewLoading(true);
        try {
          const res = await salonService.renew(selectedSalonId, months);
          setSalonDetails(res.data.data);
          showToast("Lisans başarıyla yenilendi.", "success");
          dispatch(fetchSalons());
        } catch (err) {
          showToast(err.response?.data?.error ?? "Lisans yenileme başarısız.", "error");
        } finally {
          setRenewLoading(false);
        }
      },
    });
  };

  const handleCloseDetails = () => {
    setSelectedSalonId(null);
    setSalonDetails(null);
    setPackageHistory([]);
  };

  const triggerSuspend = (id) => {
    setConfirmModal({
      isOpen: true,
      title: "Salonu Askıya Al",
      message: "Bu salonu askıya almak istediğinize emin misiniz? Salon kullanıcıları askı süresince sisteme erişemeyecektir.",
      type: "warning",
      onConfirm: () => {
        dispatch(changeSalonStatus({ id, status: "SUSPENDED" }))
          .unwrap()
          .then(() => showToast("Salon başarıyla askıya alındı.", "success"))
          .catch((err) => showToast(err || "İşlem başarısız oldu.", "error"));
      },
    });
  };

  const triggerActivate = (id) => {
    setConfirmModal({
      isOpen: true,
      title: "Salonu Aktifleştir",
      message: "Bu salonu aktifleştirmek istediğinize emin misiniz? Salon kullanıcıları sisteme tekrar tam erişim sağlayacaktır.",
      type: "success",
      onConfirm: () => {
        dispatch(changeSalonStatus({ id, status: "ACTIVE" }))
          .unwrap()
          .then(() => showToast("Salon başarıyla aktifleştirildi.", "success"))
          .catch((err) => showToast(err || "İşlem başarısız oldu.", "error"));
      },
    });
  };

  const triggerDelete = (id) => {
    setConfirmModal({
      isOpen: true,
      title: "Salonu Sil",
      message: "Bu salonu silmek istediğinize emin misiniz? Bu işlem salonu sistemden kaldıracaktır.",
      type: "danger",
      onConfirm: () => {
        dispatch(deleteSalon(id))
          .unwrap()
          .then(() => showToast("Salon başarıyla silindi.", "success"))
          .catch((err) => showToast(err || "İşlem başarısız oldu.", "error"));
      },
    });
  };

  // Define table columns
  const columns = [
    {
      key: "name",
      header: "Salon",
      type: "string",
      sortable: true,
      render: (val, row) => (
        <div>
          <span className="font-semibold text-on-surface block">{val}</span>
          <span className="text-[11px] text-on-surface-variant block">Lisans: {row.licenseNo}</span>
        </div>
      ),
    },
    {
      key: "address",
      header: "Adres",
      type: "string",
      sortable: true,
      render: (val) => val || "—",
    },
    {
      key: "packageName",
      header: "Paket",
      type: "string",
      sortable: true,
      render: (val) => val ?? "—",
    },
    {
      key: "licenseExpiryDate",
      header: "Lisans Bitiş",
      type: "string",
      sortable: true,
      render: (val) => {
        const b = licenseBadge(val);
        return (
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${b.cls}`}>
            {b.text}
          </span>
        );
      },
    },
    {
      key: "status",
      header: "Durum",
      type: "string",
      sortable: true,
      render: (val) => (
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[val] ?? ""}`}>
          {STATUS_LABELS[val] ?? val}
        </span>
      ),
    },
  ];

  // Action column renderer
  const renderActions = (s) => {
    return (
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => handleOpenDetails(s.id)}
          title="Detay Kartı"
          className="p-1.5 rounded-lg text-primary hover:bg-primary/10 transition"
        >
          <Icon name="visibility" className="!text-[18px]" />
        </button>

        {isAdmin && (
          <>
            {s.status !== "SUSPENDED" ? (
              <button
                onClick={() => triggerSuspend(s.id)}
                title="Askıya Al"
                className="p-1.5 rounded-lg text-tier-gold hover:bg-tier-gold/10 transition"
              >
                <Icon name="pause_circle" className="!text-[18px]" />
              </button>
            ) : (
              <button
                onClick={() => triggerActivate(s.id)}
                title="Aktifleştir"
                className="p-1.5 rounded-lg text-success hover:bg-success/10 transition"
              >
                <Icon name="play_circle" className="!text-[18px]" />
              </button>
            )}

            <button
              onClick={() => triggerDelete(s.id)}
              title="Sil"
              className="p-1.5 rounded-lg text-error hover:bg-error/10 transition"
            >
              <Icon name="delete" className="!text-[18px]" />
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          <Icon name="storefront" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-on-surface">
            {isAdmin ? "Salonlar" : "Salonum"}
          </h1>
          <p className="text-on-surface-variant text-sm">
            {isAdmin
              ? "Tüm salonları görüntüle, filtrele ve durumlarını yönet."
              : "Salonunuzun bilgileri."}
          </p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3">
          <Icon name="error" className="!text-[18px]" /> {error}
        </div>
      )}

      {isLoading ? (
        <div className="bg-surface-card rounded-3xl p-8 border border-outline-variant text-center text-on-surface-variant">
          <p>Yükleniyor…</p>
        </div>
      ) : (
        <AdvancedTable
          columns={columns}
          data={items}
          actions={renderActions}
          searchPlaceholder="Salonlarda ara..."
        />
      )}

      {/* Details Card Modal */}
      {selectedSalonId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-surface-card rounded-3xl shadow-modal border border-outline-variant max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-outline-variant flex items-center justify-between bg-surface-container-low">
              <div className="flex items-center gap-2">
                <Icon name="storefront" className="text-primary !text-[24px]" />
                <h3 className="font-bold text-lg text-on-surface">Salon Detay Kartı</h3>
              </div>
              <button
                onClick={handleCloseDetails}
                className="p-1 rounded-full text-on-surface-variant hover:text-primary transition"
              >
                <Icon name="close" className="!text-[22px]" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6 flex-1 text-sm">
              {detailsLoading ? (
                <div className="py-8 text-center text-on-surface-variant">Detaylar yükleniyor…</div>
              ) : detailsError ? (
                <div className="text-sm text-error bg-error/10 border border-error/20 rounded-xl px-4 py-3 flex items-center gap-2">
                  <Icon name="error" className="!text-[18px]" /> {detailsError}
                </div>
              ) : (
                salonDetails && (
                  <div className="space-y-6">
                    {/* Salon Header Information */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-primary/5 border border-primary/10 rounded-2xl p-4">
                      <div>
                        <h4 className="text-xl font-bold text-primary">{salonDetails.name}</h4>
                        <p className="text-xs text-on-surface-variant mt-0.5">
                          Lisans No: <strong className="text-on-surface mr-3">{salonDetails.licenseNo}</strong>
                          {salonDetails.taxNumber && (
                            <span>Vergi No: <strong className="text-on-surface">{salonDetails.taxNumber}</strong></span>
                          )}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[salonDetails.status] ?? ""}`}>
                        {STATUS_LABELS[salonDetails.status] ?? salonDetails.status}
                      </span>
                    </div>

                    {/* Quick Info Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-surface-container-low rounded-2xl p-4 space-y-3">
                        <h5 className="font-bold text-xs uppercase tracking-wider text-on-surface-variant flex items-center gap-1">
                          <Icon name="person" className="!text-[16px]" /> İletişim & Sahip
                        </h5>
                        <div className="space-y-1.5 text-xs text-on-surface">
                          <p><strong>Sahibi:</strong> {salonDetails.ownerName ?? "—"}</p>
                          <p><strong>Salon E-posta:</strong> {salonDetails.email ?? "—"}</p>
                          <p><strong>Salon Telefon:</strong> {salonDetails.phone ?? "—"}</p>
                          <p><strong>Websitesi:</strong> {salonDetails.website ? <a href={salonDetails.website} target="_blank" rel="noreferrer" className="text-primary hover:underline">{salonDetails.website}</a> : "—"}</p>
                        </div>
                      </div>

                      <div className="bg-surface-container-low rounded-2xl p-4 space-y-3">
                        <h5 className="font-bold text-xs uppercase tracking-wider text-on-surface-variant flex items-center gap-1">
                          <Icon name="location_on" className="!text-[16px]" /> Konum & Lisans
                        </h5>
                        <div className="space-y-1.5 text-xs text-on-surface">
                          <p><strong>Adres:</strong> {salonDetails.address ?? "—"}</p>
                          <p><strong>Lisans Başlangıç:</strong> {salonDetails.licenseIssueDate ? new Date(salonDetails.licenseIssueDate).toLocaleDateString("tr-TR") : "—"}</p>
                          <p><strong>Lisans Bitiş:</strong> {salonDetails.licenseExpiryDate ? new Date(salonDetails.licenseExpiryDate).toLocaleDateString("tr-TR") : "—"}</p>
                        </div>
                      </div>
                    </div>

                    {salonDetails.description && (
                      <div className="bg-surface-container-low rounded-2xl p-4 space-y-2">
                        <h5 className="font-bold text-xs uppercase tracking-wider text-on-surface-variant">Açıklama</h5>
                        <p className="text-xs text-on-surface-variant leading-relaxed">{salonDetails.description}</p>
                      </div>
                    )}

                    {/* Subscription & History Tracking */}
                    <div className="space-y-3">
                      <h4 className="font-bold text-sm text-on-surface flex items-center gap-1">
                        <Icon name="history" className="text-primary" /> Paket Geçmişi ve Takibi
                      </h4>
                      <div className="border border-outline-variant rounded-2xl overflow-hidden bg-white">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-surface-container-low text-on-surface-variant border-b border-outline-variant font-semibold">
                            <tr>
                              <th className="px-4 py-2">Paket</th>
                              <th className="px-4 py-2">Başlangıç</th>
                              <th className="px-4 py-2">Bitiş</th>
                              <th className="px-4 py-2 text-right">Aylık Ücret</th>
                              <th className="px-4 py-2 text-right">Kullanım Süresi</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-outline-variant">
                            {packageHistory.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="px-4 py-3 text-center text-on-surface-variant">
                                  Geçmiş bulunamadı.
                                </td>
                              </tr>
                            ) : (
                              packageHistory.map((hist) => (
                                <tr key={hist.id} className="hover:bg-primary/5 transition-colors">
                                  <td className="px-4 py-2.5 font-semibold text-on-surface">
                                    {hist.packageName}
                                    {!hist.endDate && (
                                      <span className="ml-2 px-1.5 py-0.5 text-[9px] bg-primary/10 text-primary border border-primary/20 rounded font-medium">Aktif</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2.5 text-on-surface-variant">
                                    {new Date(hist.startDate).toLocaleDateString("tr-TR")}
                                  </td>
                                  <td className="px-4 py-2.5 text-on-surface-variant">
                                    {hist.endDate ? new Date(hist.endDate).toLocaleDateString("tr-TR") : "Devam ediyor"}
                                  </td>
                                  <td className="px-4 py-2.5 text-on-surface-variant text-right font-medium text-primary">
                                    {hist.price != null ? `${Number(hist.price).toLocaleString("tr-TR")} ₺` : "-"}
                                  </td>
                                  <td className="px-4 py-2.5 text-on-surface-variant text-right font-medium">
                                    {hist.durationDays} Gün
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Admin License Renewal Controls */}
                    {isAdmin && (
                      <div className="bg-surface-container-low border border-outline-variant rounded-2xl p-4 space-y-4">
                        <h4 className="font-bold text-xs uppercase tracking-wider text-on-surface-variant flex items-center gap-1">
                          <Icon name="event_repeat" className="!text-[16px] text-primary" /> Lisans Yenile
                        </h4>
                        <form onSubmit={handleRenewSubmit} className="flex flex-col sm:flex-row gap-3 items-end">
                          <div className="flex-1">
                            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Süre</label>
                            <select
                              value={renewMonths}
                              onChange={(e) => setRenewMonths(e.target.value)}
                              className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-white focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                            >
                              <option value={1}>1 ay</option>
                              <option value={3}>3 ay</option>
                              <option value={6}>6 ay</option>
                              <option value={12}>12 ay</option>
                              <option value={24}>24 ay</option>
                            </select>
                          </div>
                          <button
                            type="submit"
                            disabled={renewLoading}
                            className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5 h-[34px] sm:h-[38px] justify-center"
                          >
                            {renewLoading ? "Yenileniyor…" : (<><Icon name="event_repeat" className="!text-[16px]" /> Lisansı Yenile</>)}
                          </button>
                        </form>
                        <p className="text-[11px] text-on-surface-variant">
                          Mevcut bitiş: <strong>{salonDetails.licenseExpiryDate ? new Date(salonDetails.licenseExpiryDate).toLocaleDateString("tr-TR") : "—"}</strong>. Süresi geçmişse bugünden, değilse mevcut bitişe eklenir.
                        </p>
                      </div>
                    )}

                    {/* Admin Upgrade Package Controls */}
                    {isAdmin && (
                      <div className="bg-surface-container-low border border-outline-variant rounded-2xl p-4 space-y-4">
                        <h4 className="font-bold text-xs uppercase tracking-wider text-on-surface-variant flex items-center gap-1">
                          <Icon name="upgrade" className="!text-[16px] text-primary" /> Paket Yükselt / Değiştir
                        </h4>

                        {salonDetails.status !== "ACTIVE" ? (
                          <div className="text-xs text-on-surface-variant bg-tier-gold/10 border border-tier-gold/20 rounded-xl px-3 py-2.5 flex items-center gap-1.5">
                            <Icon name="lock" className="!text-[16px] text-tier-gold" />
                            Paket yükseltme yalnızca <strong className="mx-1">aktif</strong> salonlarda yapılabilir.
                          </div>
                        ) : (
                        <>
                        {upgradeError && (
                          <div className="text-xs text-error bg-error/10 border border-error/20 rounded-xl px-3 py-2 flex items-center gap-1.5">
                            <Icon name="error" className="!text-[16px]" /> {upgradeError}
                          </div>
                        )}

                        <form onSubmit={handleUpgradeSubmit} className="flex flex-col sm:flex-row gap-3 items-end">
                          <div className="flex-1">
                            <label className="block text-xs font-semibold mb-1 text-on-surface-variant">Yeni Paket Seçin</label>
                            <select
                              value={upgradePackageId}
                              onChange={(e) => setUpgradePackageId(e.target.value)}
                              required
                              className="w-full border border-outline-variant rounded-xl px-3 py-2 text-xs bg-white focus:ring-4 focus:ring-primary/15 focus:border-primary outline-none transition"
                            >
                              <option value="">Paket seçin…</option>
                              {packages
                                .filter((p) => p.id !== salonDetails.packageId)
                                .map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name} — {p.monthlyPrice}₺/ay
                                  </option>
                                ))}
                            </select>
                          </div>
                          <button
                            type="submit"
                            disabled={upgradeLoading || !upgradePackageId}
                            className="bg-primary text-white rounded-xl px-5 py-2 font-semibold text-xs hover:opacity-90 transition disabled:opacity-50 flex items-center gap-1.5 h-[34px] sm:h-[38px] justify-center"
                          >
                            {upgradeLoading ? "Güncelleniyor…" : (<><Icon name="upgrade" className="!text-[16px]" /> Paketi Güncelle</>)}
                          </button>
                        </form>
                        </>
                        )}
                      </div>
                    )}
                  </div>
                )
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-outline-variant bg-surface-container-low flex justify-end">
              <button
                onClick={handleCloseDetails}
                className="border border-outline-variant bg-white text-on-surface hover:bg-surface-container-high transition rounded-xl px-4 py-2 font-semibold text-xs"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        onConfirm={confirmModal.onConfirm}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
}
